import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Layers, Plus, Search, Ticket, UserCog } from 'lucide-react';
import { adminInviteCodes, adminTradingGroups, adminUsers } from '@/api/admin';
import type { InviteCode, VirtualUserRow } from '@/api/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { fmtDateTime } from '@/lib/format';
import { ApiError } from '@/lib/http';

export function InviteCodes() {
  const qc = useQueryClient();

  // ─── 列表查询 ─────────────────────────────────────────────────
  const listQ = useQuery({
    queryKey: ['admin', 'invite-codes'],
    queryFn: () => adminInviteCodes.list({ pageSize: 100 }),
  });
  const items = listQ.data?.items ?? [];

  // ─── 新增弹窗状态 ──────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [newAlias, setNewAlias] = useState('');

  const createMutation = useMutation({
    mutationFn: () => adminInviteCodes.create({ alias: newAlias.trim() || undefined }),
    onSuccess: (created) => {
      toast.success('邀请码已创建', { description: `邀请码:${created.code}` });
      void qc.invalidateQueries({ queryKey: ['admin', 'invite-codes'] });
      setCreateOpen(false);
      setNewAlias('');
    },
    onError: (err) =>
      toast.error('创建失败', { description: err instanceof ApiError ? err.message : '未知错误' }),
  });

  // ─── 改别名弹窗状态 ────────────────────────────────────────────
  const [editTarget, setEditTarget] = useState<InviteCode | null>(null);
  const [editAlias, setEditAlias] = useState('');

  function openEdit(item: InviteCode) {
    setEditTarget(item);
    setEditAlias(item.alias ?? '');
  }

  const aliasMutation = useMutation({
    mutationFn: () =>
      adminInviteCodes.update(editTarget!.id, { alias: editAlias.trim() || undefined }),
    onSuccess: () => {
      toast.success('别名已更新');
      void qc.invalidateQueries({ queryKey: ['admin', 'invite-codes'] });
      setEditTarget(null);
    },
    onError: (err) =>
      toast.error('更新失败', { description: err instanceof ApiError ? err.message : '未知错误' }),
  });

  // ─── 停用二次确认弹窗状态 ──────────────────────────────────────
  // 停用不只是"不让新人注册":该码下**已注册的全部用户**会默认从所有分润统计中消失
  // (管理端月度分润报表与合计、平台当月分润、组长 App 的本组分润;含仍在岗、当月正常交易的人),
  // 后果是财务级的,故只对「停用」方向加确认;启用方向保持一键。
  const [disableTarget, setDisableTarget] = useState<InviteCode | null>(null);

  // ─── 启停 mutation ─────────────────────────────────────────────
  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      adminInviteCodes.update(id, { enabled }),
    onSuccess: (_, vars) => {
      toast.success(
        vars.enabled ? '邀请码已启用' : '邀请码已停用',
        vars.enabled
          ? undefined
          : {
              description:
                '该码不再接受新注册;其名下已注册用户默认不再计入任何分润统计(月度分润报表与合计、平台当月分润、组长 App 的本组分润),可在报表页打开「显示已停用渠道 / 已归档人员」拉回核对',
            },
      );
      void qc.invalidateQueries({ queryKey: ['admin', 'invite-codes'] });
      setDisableTarget(null);
    },
    onError: (err) =>
      toast.error('操作失败', { description: err instanceof ApiError ? err.message : '未知错误' }),
  });

  // ─── 设负责人弹窗状态 ──────────────────────────────────────────
  const [leaderTarget, setLeaderTarget] = useState<InviteCode | null>(null);

  // ─── 设时段组弹窗状态 ──────────────────────────────────────────
  const [groupTarget, setGroupTarget] = useState<InviteCode | null>(null);

  return (
    <div className="p-6 space-y-4">
      {/* 页头 */}
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-ink-0 flex items-center gap-2">
            <Ticket className="h-5 w-5 text-brand" />
            邀请码管理
          </h1>
          <p className="text-sm text-ink-2 mt-1">
            每个邀请码对应一个渠道/用户组,新用户注册时填写;可设别名区分来源、指派负责人(组长)在 App 端查看本组考核与分润。
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          新增邀请码
        </Button>
      </header>

      {/* 列表 */}
      <Card>
        <CardHeader>
          <CardTitle>邀请码列表 · {listQ.data?.total ?? 0}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg-3 text-ink-2 text-xs">
                <tr>
                  <th className="px-3 py-2 text-left font-normal">邀请码</th>
                  <th className="px-3 py-2 text-left font-normal">渠道别名</th>
                  <th className="px-3 py-2 text-left font-normal">负责人</th>
                  <th
                    className="px-3 py-2 text-left font-normal"
                    title="该渠道下用户默认所属的交易时段组;未绑定 = 继承全局交易时段配置。用户个人覆盖优先于此"
                  >
                    时段组
                  </th>
                  {/* 「已注册人数」会被读成"当前在册人数";used_count 只增不减,是累计值。 */}
                  <th
                    className="px-3 py-2 text-right font-normal"
                    title="累计注册人数:注册成功即 +1,不随停用 / 归档递减,故不等于当前在册人数"
                  >
                    累计注册
                  </th>
                  <th className="px-3 py-2 text-left font-normal">状态</th>
                  <th className="px-3 py-2 text-left font-normal">创建时间</th>
                  <th className="px-3 py-2 text-left font-normal">操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-t border-line row-hover">
                    <td className="px-3 py-2 font-mono font-semibold tracking-widest text-brand-dim">
                      {item.code}
                    </td>
                    <td className="px-3 py-2 text-ink-1">
                      {item.alias ?? <span className="text-ink-3">—</span>}
                    </td>
                    <td className="px-3 py-2 text-ink-1">
                      {item.leaderUserId ? (
                        <span title={`用户ID ${item.leaderUserId}`}>
                          {item.leaderNickname ?? item.leaderPhone ?? item.leaderUserId}
                        </span>
                      ) : (
                        <span className="text-ink-3">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-ink-1">
                      {item.tradingGroupId ? (
                        <span title={`时段组 id ${item.tradingGroupId}`}>{item.tradingGroupName ?? item.tradingGroupId}</span>
                      ) : (
                        <span className="text-ink-3" title="未绑定时段组:该码下用户继承全局交易时段配置">
                          继承全局
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right num">{item.usedCount}</td>
                    <td className="px-3 py-2">
                      {item.enabled ? (
                        <Badge variant="outline" className="text-up border-up/40 bg-up/10">
                          启用
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-ink-3 border-line bg-bg-3">
                          停用
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-ink-2 text-xs num">{fmtDateTime(item.createdAt)}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEdit(item)}
                        >
                          改别名
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setLeaderTarget(item)}
                        >
                          <UserCog className="h-3.5 w-3.5" />
                          设负责人
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setGroupTarget(item)}
                        >
                          <Layers className="h-3.5 w-3.5" />
                          设时段组
                        </Button>
                        <Button
                          variant={item.enabled ? 'warn' : 'outline'}
                          size="sm"
                          disabled={toggleMutation.isPending}
                          onClick={() => {
                            // 停用 = 财务级后果(该码下全部在岗用户从所有分润统计中消失)→ 走二次确认;
                            // 启用只是把人放回默认口径,保持一键。
                            if (item.enabled) setDisableTarget(item);
                            else toggleMutation.mutate({ id: item.id, enabled: true });
                          }}
                        >
                          {item.enabled ? '停用' : '启用'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!items.length && !listQ.isLoading && (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center text-ink-3">
                      暂无邀请码,点击「新增邀请码」创建第一个渠道
                    </td>
                  </tr>
                )}
                {listQ.isLoading && (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center text-ink-3">
                      加载中...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* 新增邀请码 Dialog */}
      <Dialog
        open={createOpen}
        onOpenChange={(v) => {
          if (!v) {
            setCreateOpen(false);
            setNewAlias('');
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5 text-brand" />
              新增邀请码
            </DialogTitle>
            <DialogDescription>
              邀请码(6 位)由后端随机生成,填写别名以区分渠道来源。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="create-alias">渠道别名(可选)</Label>
              <Input
                id="create-alias"
                placeholder="渠道别名,如 微信公众号"
                value={newAlias}
                onChange={(e) => setNewAlias(e.target.value)}
                disabled={createMutation.isPending}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !createMutation.isPending) createMutation.mutate();
                }}
              />
              <p className="text-xs text-ink-3">别名仅供管理员识别渠道,用户不可见</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreateOpen(false);
                setNewAlias('');
              }}
              disabled={createMutation.isPending}
            >
              取消
            </Button>
            <Button onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>
              {createMutation.isPending ? '创建中...' : '创建'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 改别名 Dialog */}
      <Dialog
        open={!!editTarget}
        onOpenChange={(v) => {
          if (!v) setEditTarget(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>修改渠道别名</DialogTitle>
            <DialogDescription>
              邀请码:{' '}
              <span className="font-mono font-semibold tracking-widest">{editTarget?.code}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="edit-alias">渠道别名(可留空)</Label>
              <Input
                id="edit-alias"
                placeholder="渠道别名,如 微信公众号"
                value={editAlias}
                onChange={(e) => setEditAlias(e.target.value)}
                disabled={aliasMutation.isPending}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !aliasMutation.isPending) aliasMutation.mutate();
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditTarget(null)}
              disabled={aliasMutation.isPending}
            >
              取消
            </Button>
            <Button onClick={() => aliasMutation.mutate()} disabled={aliasMutation.isPending}>
              {aliasMutation.isPending ? '保存中...' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 停用确认 Dialog(仅停用方向;启用一键) */}
      <Dialog
        open={!!disableTarget}
        onOpenChange={(v) => {
          if (!v && !toggleMutation.isPending) setDisableTarget(null);
        }}
      >
        <DialogContent className="max-w-md">
          {disableTarget && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-warn" />
                  停用邀请码{' '}
                  <span className="font-mono font-semibold tracking-widest">
                    {disableTarget.code}
                  </span>
                </DialogTitle>
                <DialogDescription>
                  停用不只是「不让新人注册」,它还会把该渠道下的人从<strong>所有分润统计</strong>里一并剔除,请确认:
                </DialogDescription>
              </DialogHeader>

              <ol className="list-decimal space-y-2 pl-5 text-sm text-ink-1">
                <li>该邀请码将<strong>不再接受新用户注册</strong>。</li>
                <li>
                  该渠道下<strong>仍在名单上的用户</strong>(含当月正常交易的人)及其金额,将
                  <strong>默认不再计入任何分润统计</strong>:「月度分润」报表(含渠道小计、总合计与 Excel 导出)、
                  「平台经营」页的「平台当月分润」,以及组长在 App 端看到的本组分润。
                  {/*
                    usedCount 只在注册成功时 +1、从不递减,也不区分已归档者 —— 它是**累计注册数**,
                    不等于"本次停用新增剔除的人数"(已归档的人本来就已被剔除)。这是标注财务后果的确认框,
                    数字必须可信,故只做事实陈述、不把它冒充成影响面。
                    这里也不改成实时查在册人数:users 列表没有按邀请码精确过滤的参数,只有跨字段模糊 search
                    (手机号/昵称/用户ID 也会命中同样的数字),查出来的数同样不可信,反而更危险。
                  */}
                  <div className="mt-1 text-xs text-ink-3">
                    该码<strong>累计注册 {disableTarget.usedCount} 人</strong>
                    (注册成功即 +1,不随停用或归档递减)。其中<strong>已归档的人本就不计入分润统计</strong>,
                    本次停用<strong>新增</strong>影响的只是仍在名单上的那部分,实际人数通常少于累计注册数;
                    确切名单请到「月度分润」页切到该渠道 Tab 查看。
                  </div>
                </li>
                <li>
                  需要核对时,可在报表页打开「显示已停用渠道 / 已归档人员」开关,把这些人连同金额一并拉回来。
                </li>
                <li>
                  <strong>建议先完成当月分润结算再停用</strong>,否则当月已产生的分润在默认视图里看不到,容易漏发。
                </li>
              </ol>

              <p className="text-xs text-ink-3">
                停用<strong>不影响</strong>这些用户登录与交易(该开关只在注册环节校验),也不删除任何历史数据;
                随时可重新「启用」恢复默认口径。
              </p>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setDisableTarget(null)}
                  disabled={toggleMutation.isPending}
                >
                  取消
                </Button>
                <Button
                  variant="warn"
                  disabled={toggleMutation.isPending}
                  onClick={() => toggleMutation.mutate({ id: disableTarget.id, enabled: false })}
                >
                  {toggleMutation.isPending ? '停用中…' : '确认停用'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* 设负责人 Dialog */}
      <LeaderDialog target={leaderTarget} onClose={() => setLeaderTarget(null)} />

      {/* 设时段组 Dialog */}
      <TradingGroupBindDialog target={groupTarget} onClose={() => setGroupTarget(null)} />
    </div>
  );
}

/**
 * 「设时段组」弹窗:把渠道(邀请码)绑到某个交易时段组。
 * 三态与负责人指派同形:选「继承全局」= 传 null 解绑,选某组 = 传该组 id;不打开弹窗则字段缺省不动。
 * 该码下**未做单用户覆盖**的全部用户会跟着换窗口与收盘时刻,故提示在收盘后操作。
 */
function TradingGroupBindDialog({ target, onClose }: { target: InviteCode | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);

  const groupsQ = useQuery({
    queryKey: ['admin', 'trading-groups'],
    queryFn: adminTradingGroups.list,
    enabled: !!target,
  });
  const groups = groupsQ.data?.items ?? [];

  useEffect(() => {
    if (target) setSelected(target.tradingGroupId ?? null);
  }, [target]);

  const save = useMutation({
    mutationFn: (tradingGroupId: string | null) =>
      adminInviteCodes.update(target!.id, { tradingGroupId }),
    onSuccess: (_, tradingGroupId) => {
      toast.success(tradingGroupId ? '时段组已绑定' : '已解绑时段组', {
        description: tradingGroupId
          ? '该码下未做单用户覆盖的用户,可交易窗口与收盘时刻即刻按新组走'
          : '该码下用户回到全局交易时段配置',
      });
      void qc.invalidateQueries({ queryKey: ['admin', 'invite-codes'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'trading-groups'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      onClose();
    },
    onError: (err) =>
      toast.error('保存失败', { description: err instanceof ApiError ? err.message : '未知错误' }),
  });

  const unchanged = (target?.tradingGroupId ?? null) === selected;

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && !save.isPending && onClose()}>
      <DialogContent className="max-w-lg">
        {target && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5 text-brand" />
                设时段组 · <span className="font-mono tracking-widest">{target.code}</span>
              </DialogTitle>
              <DialogDescription>
                该渠道下用户默认所属的交易时段组。解析优先级:<strong>用户个人覆盖 → 本渠道 → 全局</strong>。
                改绑<strong>立即生效</strong>,会改变该码下全部未做个人覆盖用户的可交易窗口与收盘时刻,
                <strong className="text-warn">请在收盘后操作</strong>;已开仓的持仓归属不回溯(历史分组日报不漂移)。
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <label className="flex items-center gap-2 rounded-md border border-line px-3 py-2 text-sm cursor-pointer row-hover">
                <input
                  type="radio"
                  name="group-pick"
                  className="h-4 w-4"
                  checked={selected === null}
                  disabled={save.isPending}
                  onChange={() => setSelected(null)}
                />
                <span className="text-ink-2">继承全局(不绑定任何组,走「交易时段配置」)</span>
              </label>

              <div className="max-h-72 overflow-y-auto rounded-md border border-line divide-y divide-line/60">
                {groupsQ.isLoading && (
                  <div className="px-3 py-8 text-center text-sm text-ink-3">加载中...</div>
                )}
                {groupsQ.isError && (
                  <div className="px-3 py-8 text-center text-sm text-down-dim">
                    无法加载时段组:{groupsQ.error instanceof ApiError ? groupsQ.error.message : '未知错误'}
                  </div>
                )}
                {!groupsQ.isLoading && !groupsQ.isError && groups.length === 0 && (
                  <div className="px-3 py-8 text-center text-sm text-ink-3">
                    还没有时段组,请先到「交易时段组」页新建
                  </div>
                )}
                {groups.map((g) => (
                  <label key={g.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer row-hover">
                    <input
                      type="radio"
                      name="group-pick"
                      className="h-4 w-4"
                      checked={selected === g.id}
                      disabled={save.isPending}
                      onChange={() => setSelected(g.id)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-ink-1">
                        {g.name}
                        {!g.enabled && <span className="ml-1.5 text-xs text-warn">(已停用,组内用户回退全局)</span>}
                      </span>
                      <span className="block truncate text-xs text-ink-3 num">
                        {g.alwaysOn
                          ? '24 小时(持续开放)'
                          : `${String(g.openHour).padStart(2, '0')}:${String(g.openMinute ?? 0).padStart(2, '0')}-${String(g.forceCloseHour).padStart(2, '0')}:${String(g.forceCloseMinute ?? 0).padStart(2, '0')} · ${g.tradingDays.join(' ')}`}
                        {' · '}
                        {g.userCount} 人
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={save.isPending}>
                取消
              </Button>
              <Button onClick={() => save.mutate(selected)} disabled={save.isPending || unchanged}>
                {save.isPending ? '保存中...' : '保存'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * 「设负责人」弹窗:拉全量注册用户(pageSize 500)+ 前端按 手机号/昵称/用户ID/渠道 过滤 + 单选;
 * 保存 PATCH invite-codes/{id} 的 leaderUserId(选中=user_id 指派 / 清除=null),成功后失效列表 query。
 */
function LeaderDialog({ target, onClose }: { target: InviteCode | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const PICK_PAGE_SIZE = 500;

  // 打开时:预选当前负责人、清空搜索。
  useEffect(() => {
    if (target) {
      setSelected(target.leaderUserId ?? null);
      setSearch('');
      setDebouncedSearch('');
    }
  }, [target]);

  // 搜索词防抖 300ms → 下发服务端过滤(避免逐字请求)。
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // 用户列表:服务端按关键词过滤(突破单页 500 上限——超过 500 个用户时,靠搜索也能选到任意用户,
  // 不再只能选到前 500 个)。dismissed 用户不能当负责人,前端再过滤掉。
  const usersQ = useQuery({
    queryKey: ['admin', 'users', 'leader-picker', debouncedSearch],
    queryFn: () =>
      adminUsers.list({
        pageSize: PICK_PAGE_SIZE,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      }),
    enabled: !!target,
    placeholderData: (prev) => prev,
  });
  const serverTotal = usersQ.data?.total ?? 0;
  const allUsers = useMemo(
    () => (usersQ.data?.items ?? []).filter((u) => u.status !== 'dismissed'),
    [usersQ.data],
  );
  // 结果被单页上限截断(仍有更多用户未加载)→ 提示用搜索缩小范围。
  const truncated = (usersQ.data?.items?.length ?? 0) >= PICK_PAGE_SIZE && serverTotal > PICK_PAGE_SIZE;

  // 客户端即时窄化(防抖窗口内先用已加载结果给反馈;服务端结果到达后覆盖)。
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allUsers;
    return allUsers.filter(
      (u) =>
        u.userId.toLowerCase().includes(q) ||
        (u.phone ?? '').toLowerCase().includes(q) ||
        (u.nickname ?? '').toLowerCase().includes(q) ||
        (u.channelAlias ?? '').toLowerCase().includes(q) ||
        (u.inviteCode ?? '').toLowerCase().includes(q),
    );
  }, [allUsers, search]);

  const save = useMutation({
    mutationFn: (leaderUserId: string | null) =>
      adminInviteCodes.update(target!.id, { leaderUserId }),
    onSuccess: (_, leaderUserId) => {
      toast.success(leaderUserId ? '负责人已指派' : '已清除负责人');
      void qc.invalidateQueries({ queryKey: ['admin', 'invite-codes'] });
      onClose();
    },
    onError: (err) =>
      toast.error('保存失败', { description: err instanceof ApiError ? err.message : '未知错误' }),
  });

  const hadLeader = !!target?.leaderUserId;
  // 无变更(仍是原负责人)时禁用保存。
  const unchanged = (target?.leaderUserId ?? null) === selected;

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && !save.isPending && onClose()}>
      <DialogContent className="max-w-lg">
        {target && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCog className="h-5 w-5 text-brand" />
                设负责人 · <span className="font-mono tracking-widest">{target.code}</span>
              </DialogTitle>
              <DialogDescription>
                指派一名注册用户为该渠道/组的负责人(组长),其可在 App 端只读查看本组考核与分润。
                单选;已解雇(dismissed)用户不可指派。
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-ink-3" />
                <Input
                  placeholder="搜索 手机号 / 昵称 / 用户ID / 渠道"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-7"
                  disabled={save.isPending}
                />
              </div>

              {/* 无负责人(清除)选项 */}
              <label
                className="flex items-center gap-2 rounded-md border border-line px-3 py-2 text-sm cursor-pointer row-hover"
                title="不指派负责人(该渠道无组长)"
              >
                <input
                  type="radio"
                  name="leader-pick"
                  className="h-4 w-4"
                  checked={selected === null}
                  disabled={save.isPending}
                  onChange={() => setSelected(null)}
                />
                <span className="text-ink-2">不指派负责人{hadLeader ? '(清除当前)' : ''}</span>
              </label>

              <div className="max-h-72 overflow-y-auto rounded-md border border-line divide-y divide-line/60">
                {usersQ.isLoading && (
                  <div className="px-3 py-8 text-center text-sm text-ink-3">加载中...</div>
                )}
                {usersQ.isError && (
                  <div className="px-3 py-8 text-center text-sm text-down-dim">
                    无法加载用户:{usersQ.error instanceof ApiError ? usersQ.error.message : '未知错误'}
                  </div>
                )}
                {!usersQ.isLoading && !usersQ.isError && filtered.length === 0 && (
                  <div className="px-3 py-8 text-center text-sm text-ink-3">没有匹配的用户</div>
                )}
                {filtered.map((u) => (
                  <UserPickRow
                    key={u.userId}
                    u={u}
                    checked={selected === u.userId}
                    disabled={save.isPending}
                    onSelect={() => setSelected(u.userId)}
                  />
                ))}
              </div>
              <p className="text-xs text-ink-3">
                共 {filtered.length} 名可选用户(已排除已解雇用户)
                {truncated && (
                  <span className="text-warn"> · 用户超过 {PICK_PAGE_SIZE},仅显示部分,请输入关键词搜索以精确查找</span>
                )}
              </p>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={save.isPending}>
                取消
              </Button>
              <Button onClick={() => save.mutate(selected)} disabled={save.isPending || unchanged}>
                {save.isPending ? '保存中...' : '保存'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function UserPickRow({
  u,
  checked,
  disabled,
  onSelect,
}: {
  u: VirtualUserRow;
  checked: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  return (
    <label className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer row-hover">
      <input
        type="radio"
        name="leader-pick"
        className="h-4 w-4"
        checked={checked}
        disabled={disabled}
        onChange={onSelect}
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-ink-1">
          {u.nickname || u.phone || `用户${u.userId}`}
        </span>
        <span className="block truncate text-xs text-ink-3 num">
          {u.phone || '无手机号'} · ID {u.userId}
          {u.channelAlias || u.inviteCode ? ` · ${u.channelAlias || u.inviteCode}` : ''}
        </span>
      </span>
    </label>
  );
}
